package com.schoolconnect.app.feature.itusers

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFullNameField
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogMobileField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.pencil

private val EditITUserDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
internal fun EditITUserModal(
    open: Boolean,
    title: String,
    subtitle: String,
    initialValues: ITUserFormValues,
    onClose: () -> Unit,
    onSave: (ITUserFormValues) -> Unit,
    saveLabel: String,
    isSaving: Boolean = false,
    apiError: String = ""
) {
    if (!open) return

    var name by remember(initialValues) { mutableStateOf(initialValues.name) }
    var mobile by remember(initialValues) { mutableStateOf(initialValues.mobile) }
    var email by remember(initialValues) { mutableStateOf(initialValues.email) }
    var role by remember(initialValues) { mutableStateOf(initialValues.role) }
    var status by remember(initialValues) { mutableStateOf(initialValues.status.ifBlank { "Active" }) }
    var hasSubmitted by remember { mutableStateOf(true) }
    var roleExpanded by remember { mutableStateOf(false) }
    var statusExpanded by remember { mutableStateOf(false) }

    val validation = remember(hasSubmitted, name, mobile, email, role, status) {
        if (hasSubmitted) {
            validateITUser(
                ITUserFormValues(
                    name = name,
                    mobile = mobile,
                    email = email,
                    role = role,
                    status = status
                )
            )
        } else {
            ITUserFormErrors()
        }
    }

    LaunchedEffect(open, initialValues) {
        if (open) {
            name = initialValues.name
            mobile = initialValues.mobile
            email = initialValues.email
            role = initialValues.role
            status = initialValues.status.ifBlank { "Active" }
            hasSubmitted = true
            roleExpanded = false
            statusExpanded = false
        }
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onClose() },
        dismissOnClickOutside = false,
        size = AppDialogSize.Standard,
        colors = EditITUserDialogColors,
        maxDialogHeight = 900.dp,
        isLoading = isSaving,
        loadingTitle = "Saving IT user"
    ) {
        AppDialogFormGrid(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
            compactBreakpoint = 680.dp
        ) {
            val compact = it
            val scrollState = rememberScrollState()
            val columnGap = if (compact) 20.dp else 40.dp
            val fieldGap = if (compact) 18.dp else 26.dp

            Column(modifier = Modifier.fillMaxWidth()) {
                AppDialogHeader(
                    title = title,
                    subtitle = subtitle,
                    icon = Res.drawable.pencil,
                    closeContentDescription = "Close edit IT user",
                    onClose = { if (!isSaving) onClose() },
                    colors = EditITUserDialogColors
                )
                AppDialogDivider(colors = EditITUserDialogColors)
                AppDialogValidationSummary(
                    errors = listOf(validation.name, validation.mobile, validation.email, validation.role, validation.status),
                    colors = EditITUserDialogColors,
                    modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
                )
                AppDialogErrorMessage(
                    message = apiError,
                    colors = EditITUserDialogColors,
                    modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
                )

                BoxWithConstraints(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f, fill = false)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .verticalScroll(scrollState)
                            .padding(horizontal = 28.dp, vertical = 24.dp),
                        verticalArrangement = Arrangement.spacedBy(fieldGap)
                    ) {
                        if (compact) {
                            Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                                EditITUserFieldColumn(label = "Full Name", required = true, error = validation.name) {
                                    EditITUserTextField(
                                        value = name,
                                        onValueChange = { name = it },
                                        placeholder = "Enter full name",
                                        error = validation.name
                                    )
                                }
                                EditITUserFieldColumn(label = "Mobile Number", required = true, error = validation.mobile) {
                                    EditITUserTextField(
                                        value = mobile,
                                        onValueChange = { mobile = it.filter(Char::isDigit).take(10) },
                                        placeholder = "Enter mobile number",
                                        error = validation.mobile
                                    )
                                }
                                EditITUserFieldColumn(label = "Email Address", required = true, error = validation.email) {
                                    EditITUserTextField(
                                        value = email,
                                        onValueChange = { email = it },
                                        placeholder = "Enter email address",
                                        error = validation.email
                                    )
                                }
                                EditITUserFieldColumn(label = "Role", required = true, error = validation.role) {
                                    EditITUserSelectField(
                                        value = role,
                                        placeholder = "Select role",
                                        expanded = roleExpanded,
                                        options = itUserRoleOptions,
                                        onToggleExpanded = { roleExpanded = !roleExpanded },
                                        onDismissExpanded = { roleExpanded = false },
                                        onSelected = {
                                            role = it
                                            roleExpanded = false
                                        },
                                        error = validation.role
                                    )
                                }
                                EditITUserFieldColumn(label = "Status", required = true, error = validation.status) {
                                    EditITUserSelectField(
                                        value = status,
                                        placeholder = "Select status",
                                        expanded = statusExpanded,
                                        options = itUserStatusOptions,
                                        onToggleExpanded = { statusExpanded = !statusExpanded },
                                        onDismissExpanded = { statusExpanded = false },
                                        onSelected = {
                                            status = it
                                            statusExpanded = false
                                        },
                                        error = validation.status
                                    )
                                }
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                                EditITUserFieldColumn(
                                    label = "Full Name",
                                    required = true,
                                    error = validation.name
                                ) {
                                    EditITUserTextField(
                                        value = name,
                                        onValueChange = { name = it },
                                        placeholder = "Enter full name",
                                        error = validation.name
                                    )
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                    EditITUserFieldColumn(
                                        label = "Mobile Number",
                                        required = true,
                                        modifier = Modifier.weight(1f),
                                        error = validation.mobile
                                    ) {
                                        EditITUserTextField(
                                            value = mobile,
                                            onValueChange = { mobile = it.filter(Char::isDigit).take(10) },
                                            placeholder = "Enter mobile number",
                                            error = validation.mobile
                                        )
                                    }
                                    EditITUserFieldColumn(
                                        label = "Email Address",
                                        required = true,
                                        modifier = Modifier.weight(1f),
                                        error = validation.email
                                    ) {
                                        EditITUserTextField(
                                            value = email,
                                            onValueChange = { email = it },
                                            placeholder = "Enter email address",
                                            error = validation.email
                                        )
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                    EditITUserFieldColumn(
                                        label = "Role",
                                        required = true,
                                        modifier = Modifier.weight(1f),
                                        error = validation.role
                                    ) {
                                        EditITUserSelectField(
                                            value = role,
                                            placeholder = "Select role",
                                            expanded = roleExpanded,
                                            options = itUserRoleOptions,
                                            onToggleExpanded = { roleExpanded = !roleExpanded },
                                            onDismissExpanded = { roleExpanded = false },
                                            onSelected = {
                                                role = it
                                                roleExpanded = false
                                            },
                                            error = validation.role
                                        )
                                    }
                                    EditITUserFieldColumn(
                                        label = "Status",
                                        required = true,
                                        modifier = Modifier.weight(1f),
                                        error = validation.status
                                    ) {
                                        EditITUserSelectField(
                                            value = status,
                                            placeholder = "Select status",
                                            expanded = statusExpanded,
                                            options = itUserStatusOptions,
                                            onToggleExpanded = { statusExpanded = !statusExpanded },
                                            onDismissExpanded = { statusExpanded = false },
                                            onSelected = {
                                                status = it
                                                statusExpanded = false
                                            },
                                            error = validation.status
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                EditITUserFooter(
                    onCancel = { if (!isSaving) onClose() },
                    onSave = {
                        hasSubmitted = true
                        val values = ITUserFormValues(
                            name = name,
                            mobile = mobile,
                            email = email,
                            role = role,
                            status = status
                        )
                        val errors = validateITUser(values)
                        if (listOf(errors.name, errors.mobile, errors.email, errors.role, errors.status).none { it.isNotBlank() } && !isSaving) {
                            onSave(values)
                        }
                    },
                    saveLabel = saveLabel,
                    isSaving = isSaving
                )
            }
        }
    }
}

@Composable
private fun EditITUserFieldColumn(
    label: String,
    required: Boolean = false,
    modifier: Modifier = Modifier,
    error: String = "",
    field: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        label = label,
        required = required,
        modifier = modifier,
        error = error,
        colors = EditITUserDialogColors,
        field = field
    )
}

@Composable
private fun EditITUserTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    error: String = ""
) {
    when {
        placeholder.equals("Enter full name", ignoreCase = true) -> AppDialogFullNameField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            colors = EditITUserDialogColors
        )
        placeholder.equals("Enter mobile number", ignoreCase = true) -> AppDialogMobileField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            colors = EditITUserDialogColors
        )
        else -> AppDialogTextField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            colors = EditITUserDialogColors
        )
    }
}

@Composable
private fun EditITUserSelectField(
    value: String,
    placeholder: String,
    expanded: Boolean,
    options: List<String>,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (String) -> Unit,
    error: String = ""
) {
    AppDialogSelectField(
        value = value,
        placeholder = placeholder,
        expanded = expanded,
        options = options,
        onToggleExpanded = onToggleExpanded,
        onDismissExpanded = onDismissExpanded,
        onSelected = onSelected,
        error = error,
        colors = EditITUserDialogColors
    )
}

@Composable
private fun EditITUserFooter(
    onCancel: () -> Unit,
    onSave: () -> Unit,
    saveLabel: String,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onCancel, width = 108.dp, enabled = !isSaving),
            AppDialogFooterAction(label = if (isSaving) "Saving..." else saveLabel, onClick = onSave, style = AppDialogActionStyle.Primary, width = 108.dp, enabled = !isSaving)
        ),
        colors = EditITUserDialogColors
    )
}
